import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SWIPE_THRESHOLD = 40; // px displacement to trigger navigation
const VELOCITY_THRESHOLD = 0.3; // px/ms velocity to trigger navigation
const RUBBER_BAND_FACTOR = 0.3; // resistance at edges

/**
 * Fullscreen image viewer overlay for chat images.
 * Swiper.js-style swipe with momentum, rubber-banding, zoom, pan, and download.
 */
export function ImageViewer({
    isOpen,
    images = [],
    currentIndex = 0,
    onNavigate,
    onClose,
}) {
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);

    // Swipe state
    const [dragOffset, setDragOffset] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [skipTransition, setSkipTransition] = useState(false);
    const isSwiping = useRef(false);
    const dragStart = useRef({ x: 0, y: 0, time: 0 });
    const posStart = useRef({ x: 0, y: 0 });
    const velocityTracker = useRef({ x: 0, time: 0 });
    const lastTap = useRef(0);
    const downTarget = useRef(null);
    const pointerMoved = useRef(false);
    const pendingClose = useRef(null);
    const containerRef = useRef(null);
    const thumbStripRef = useRef(null);

    const current = images[currentIndex] || null;
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < images.length - 1;

    // Reset zoom/pan/swipe when image changes or viewer opens
    useEffect(() => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
        setDragOffset(0);
    }, [isOpen, currentIndex]);

    // Scroll active thumbnail into view
    useEffect(() => {
        if (!thumbStripRef.current) return;
        const activeEl = thumbStripRef.current.children[currentIndex];
        if (activeEl) {
            activeEl.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
        }
    }, [currentIndex]);

    // Keyboard: Escape, ArrowLeft, ArrowRight
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft" && hasPrev) onNavigate(currentIndex - 1);
            if (e.key === "ArrowRight" && hasNext) onNavigate(currentIndex + 1);
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [isOpen, onClose, onNavigate, currentIndex, hasPrev, hasNext]);

    const handleDoubleClick = useCallback(() => {
        if (scale > 1) {
            setScale(1);
            setPosition({ x: 0, y: 0 });
        } else {
            setScale(2.5);
        }
    }, [scale]);

    const handleWheel = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setScale((prev) => Math.min(Math.max(prev - e.deltaY * 0.002, 0.5), 5));
    }, []);

    const handlePointerDown = useCallback(
        (e) => {
            if (isAnimating) return;

            // A second tap is arriving — this is a double-tap-to-zoom, not a
            // request to close. Cancel the close the first tap scheduled.
            if (pendingClose.current) {
                clearTimeout(pendingClose.current);
                pendingClose.current = null;
            }

            // Presses that start on a control belong to the control. Capturing
            // here would RETARGET the eventual click to this container (that is
            // what pointer capture does), so the button's own onClick never
            // fires — the prev/next arrows were silently dead because of it.
            if (e.target?.closest?.("button, a")) return;

            downTarget.current = e.target;
            pointerMoved.current = false;
            const now = Date.now();
            dragStart.current = { x: e.clientX, y: e.clientY, time: now };
            velocityTracker.current = { x: e.clientX, time: now };

            if (scale > 1) {
                setIsDragging(true);
                posStart.current = { ...position };
            } else {
                isSwiping.current = true;
            }
            e.currentTarget.setPointerCapture(e.pointerId);
        },
        [scale, position, isAnimating],
    );

    const handlePointerMove = useCallback(
        (e) => {
            if (
                Math.abs(e.clientX - dragStart.current.x) > 6 ||
                Math.abs(e.clientY - dragStart.current.y) > 6
            ) {
                pointerMoved.current = true;
            }

            if (isDragging && scale > 1) {
                setPosition({
                    x: posStart.current.x + (e.clientX - dragStart.current.x),
                    y: posStart.current.y + (e.clientY - dragStart.current.y),
                });
            } else if (isSwiping.current && scale <= 1) {
                let dx = e.clientX - dragStart.current.x;

                // Rubber-band at edges
                if ((dx > 0 && !hasPrev) || (dx < 0 && !hasNext)) {
                    dx *= RUBBER_BAND_FACTOR;
                }

                setDragOffset(dx);

                // Track velocity (use last two points)
                velocityTracker.current = { x: e.clientX, time: Date.now() };
            }
        },
        [isDragging, scale, hasPrev, hasNext],
    );

    const handlePointerUp = useCallback(
        (e) => {
            if (isDragging) {
                setIsDragging(false);
                return;
            }
            if (isSwiping.current) {
                isSwiping.current = false;
                const dx = e.clientX - dragStart.current.x;
                const dt = Date.now() - dragStart.current.time;
                const velocity = dt > 0 ? dx / dt : 0; // px/ms

                let navigated = false;

                // Navigate if velocity OR displacement exceeds threshold
                if (
                    (Math.abs(velocity) > VELOCITY_THRESHOLD || Math.abs(dx) > SWIPE_THRESHOLD) &&
                    !((dx > 0 && !hasPrev) || (dx < 0 && !hasNext))
                ) {
                    if (dx < 0 && hasNext) {
                        setIsAnimating(true);
                        const width = containerRef.current?.offsetWidth || 300;
                        setDragOffset(-width);
                        setTimeout(() => {
                            // Skip transition so offset resets instantly with no flicker
                            setSkipTransition(true);
                            setDragOffset(0);
                            onNavigate(currentIndex + 1);
                            setIsAnimating(false);
                            requestAnimationFrame(() => setSkipTransition(false));
                        }, 280);
                        navigated = true;
                    } else if (dx > 0 && hasPrev) {
                        setIsAnimating(true);
                        const width = containerRef.current?.offsetWidth || 300;
                        setDragOffset(width);
                        setTimeout(() => {
                            setSkipTransition(true);
                            setDragOffset(0);
                            onNavigate(currentIndex - 1);
                            setIsAnimating(false);
                            requestAnimationFrame(() => setSkipTransition(false));
                        }, 280);
                        navigated = true;
                    }
                }

                if (!navigated) {
                    // Snap back
                    setDragOffset(0);

                    // A stationary tap. Close only when it began OUTSIDE the
                    // picture — the letterbox is "outside", the photo is not.
                    // Decided from the pointerdown origin, never from click
                    // targets: setPointerCapture retargets the synthesized
                    // click to this container, so click.target lies.
                    //
                    // Deferred one beat so a double-tap can cancel it and zoom
                    // instead of closing on its first tap.
                    const tappedImage = downTarget.current?.tagName === "IMG";
                    if (!tappedImage && !pointerMoved.current && scale <= 1) {
                        if (pendingClose.current) {
                            clearTimeout(pendingClose.current);
                        }
                        pendingClose.current = setTimeout(() => {
                            pendingClose.current = null;
                            onClose();
                        }, 300);
                    }
                }
            }
        },
        [isDragging, hasNext, hasPrev, onNavigate, currentIndex, scale, onClose],
    );

    // Never fire a scheduled close after unmount.
    useEffect(
        () => () => {
            if (pendingClose.current) clearTimeout(pendingClose.current);
        },
        [],
    );

    const handleTouchEnd = useCallback(
        (e) => {
            const now = Date.now();
            if (now - lastTap.current < 300) {
                e.preventDefault();
                handleDoubleClick();
            }
            lastTap.current = now;
        },
        [handleDoubleClick],
    );

    const handleDownload = useCallback(() => {
        if (!current?.src) return;
        const link = document.createElement("a");
        link.href = current.src;
        link.download = current.alt || "image";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.click();
    }, [current]);

    const zoomIn = useCallback(() => setScale((prev) => Math.min(prev + 0.5, 5)), []);
    const zoomOut = useCallback(() => {
        setScale((prev) => {
            const next = Math.max(prev - 0.5, 0.5);
            if (next <= 1) setPosition({ x: 0, y: 0 });
            return next;
        });
    }, []);
    const resetZoom = useCallback(() => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    }, []);

    const goPrev = useCallback(() => {
        if (hasPrev && !isAnimating) {
            setIsAnimating(true);
            const width = containerRef.current?.offsetWidth || 300;
            setDragOffset(width);
            setTimeout(() => {
                setSkipTransition(true);
                setDragOffset(0);
                onNavigate(currentIndex - 1);
                setIsAnimating(false);
                requestAnimationFrame(() => setSkipTransition(false));
            }, 280);
        }
    }, [hasPrev, onNavigate, currentIndex, isAnimating]);

    const goNext = useCallback(() => {
        if (hasNext && !isAnimating) {
            setIsAnimating(true);
            const width = containerRef.current?.offsetWidth || 300;
            setDragOffset(-width);
            setTimeout(() => {
                setSkipTransition(true);
                setDragOffset(0);
                onNavigate(currentIndex + 1);
                setIsAnimating(false);
                requestAnimationFrame(() => setSkipTransition(false));
            }, 280);
        }
    }, [hasNext, onNavigate, currentIndex, isAnimating]);

    // Compute the slide strip transform
    const containerWidth = containerRef.current?.offsetWidth || 0;
    const isInteracting = isSwiping.current || isDragging;

    return (
        <AnimatePresence>
            {isOpen && current && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="tw-absolute tw-inset-0 tw-z-[100] tw-flex tw-flex-col"
                    style={{ background: "rgba(0,0,0,0.92)" }}
                >
                    {/* Top bar */}
                    <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-flex-shrink-0">
                        <div className="tw-flex tw-items-center tw-gap-1">
                            <button onClick={zoomOut} className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-transition-colors" aria-label="Zoom out">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                                    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35M8 11h6" />
                                </svg>
                            </button>
                            <button onClick={resetZoom} className="tw-px-2 tw-h-8 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-border-0 tw-cursor-pointer tw-text-white/80 tw-text-[11px] tw-font-medium tw-transition-colors" aria-label="Reset zoom">
                                {Math.round(scale * 100)}%
                            </button>
                            <button onClick={zoomIn} className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-transition-colors" aria-label="Zoom in">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                                    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
                                </svg>
                            </button>
                            {images.length > 1 && (
                                <span className="tw-text-white/50 tw-text-[11px] tw-ml-2">
                                    {currentIndex + 1} / {images.length}
                                </span>
                            )}
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-1">
                            <button onClick={handleDownload} className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-transition-colors" aria-label="Download image">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                                </svg>
                            </button>
                            <a href={current.src} target="_blank" rel="noopener noreferrer" className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-transition-colors tw-no-underline" aria-label="Open in new tab">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                                </svg>
                            </a>
                            <button onClick={onClose} className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-transition-colors" aria-label="Close viewer">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Image area — horizontal slide strip */}
                    <div
                        ref={containerRef}
                        className="tw-flex-1 tw-overflow-hidden tw-select-none tw-relative"
                        style={{ cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default", touchAction: "none" }}
                        onDoubleClick={handleDoubleClick}
                        onWheel={handleWheel}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerLeave={handlePointerUp}
                        onTouchEnd={handleTouchEnd}

                    >
                        {/* Prev arrow */}
                        {hasPrev && scale <= 1 && (
                            <button
                                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                                className="tw-absolute tw-left-1 tw-top-1/2 tw--translate-y-1/2 tw-w-9 tw-h-9 tw-rounded-full tw-bg-black/50 hover:tw-bg-black/70 tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-transition-colors tw-z-10"
                                aria-label="Previous image"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M15 18l-6-6 6-6" />
                                </svg>
                            </button>
                        )}

                        {/* Slide strip: prev + current + next */}
                        <div
                            className="tw-flex tw-h-full tw-w-full"
                            style={{
                                transform: `translateX(${dragOffset}px)`,
                                transition: (isSwiping.current || skipTransition) ? "none" : "transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                                willChange: "transform",
                            }}
                        >
                            {/* Previous image (offscreen left) */}
                            <div
                                className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center"
                                style={{ transform: "translateX(-100%)" }}
                            >
                                {hasPrev && images[currentIndex - 1] && (
                                    <img
                                        src={images[currentIndex - 1].src}
                                        alt={images[currentIndex - 1].alt || ""}
                                        className="tw-max-w-full tw-max-h-full tw-object-contain tw-pointer-events-none"
                                        draggable={false}
                                    />
                                )}
                            </div>

                            {/* Current image */}
                            <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <img
                                    src={current.src}
                                    alt={current.alt || "Image"}
                                    className="tw-max-w-full tw-max-h-full tw-object-contain"
                                    style={{
                                        touchAction: "none",
                                        ...(scale > 1
                                            ? {
                                                  transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                                                  transition: isDragging ? "none" : "transform 0.2s ease",
                                              }
                                            : {}),
                                    }}
                                    draggable={false}
                                />
                            </div>

                            {/* Next image (offscreen right) */}
                            <div
                                className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center"
                                style={{ transform: "translateX(100%)" }}
                            >
                                {hasNext && images[currentIndex + 1] && (
                                    <img
                                        src={images[currentIndex + 1].src}
                                        alt={images[currentIndex + 1].alt || ""}
                                        className="tw-max-w-full tw-max-h-full tw-object-contain tw-pointer-events-none"
                                        draggable={false}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Next arrow */}
                        {hasNext && scale <= 1 && (
                            <button
                                onClick={(e) => { e.stopPropagation(); goNext(); }}
                                className="tw-absolute tw-right-1 tw-top-1/2 tw--translate-y-1/2 tw-w-9 tw-h-9 tw-rounded-full tw-bg-black/50 hover:tw-bg-black/70 tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-transition-colors tw-z-10"
                                aria-label="Next image"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 18l6-6-6-6" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* Thumbnail strip */}
                    {images.length > 1 && (
                        <div className="tw-flex-shrink-0 tw-px-3 tw-pb-3 tw-pt-1">
                            <div
                                ref={thumbStripRef}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-overflow-x-auto tw-justify-center no-scrollbar"
                                style={{ scrollBehavior: "smooth" }}
                            >
                                {images.map((img, idx) => (
                                    <div
                                        key={img.messageId || idx}
                                        onClick={() => onNavigate(idx)}
                                        className={`tw-flex-shrink-0 tw-w-11 tw-h-11 tw-rounded-lg tw-overflow-hidden tw-cursor-pointer tw-transition-all tw-border-2 ${
                                            idx === currentIndex
                                                ? "tw-border-white tw-opacity-100"
                                                : "tw-border-transparent tw-opacity-50 hover:tw-opacity-80"
                                        }`}
                                    >
                                        <img
                                            src={img.src}
                                            alt={img.alt || ""}
                                            className="tw-w-full tw-h-full tw-object-cover"
                                            draggable={false}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
}
